import { PhoneIcon, MapPinIcon, StarIcon, ClockIcon } from "@heroicons/react/24/solid";
import { CheckBadgeIcon, GlobeAltIcon } from "@heroicons/react/24/outline";
import type { Firm } from "~/lib/firms";

type Props = {
  firm: Firm;
};

const FirmCard: React.FC<Props> = ({ firm }) => {
  const fullStars = firm.rating != null ? Math.floor(firm.rating) : 0;
  const hasHalf = firm.rating != null && firm.rating % 1 >= 0.5;

  return (
    <div className="print-firm-card flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow duration-200 hover:shadow-md">
      {/* Header */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900 leading-snug">
          {firm.name}
        </h3>
        {firm.hours24 && (
          <span className="shrink-0 rounded-full bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm">
            Czynne 24/7
          </span>
        )}
      </div>


      {/* Rating */}
      {firm.rating != null ? (
      <div className="mb-3 flex items-center gap-2">
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }, (_, i) => (
            <StarIcon
              key={i}
              className={`h-4 w-4 ${
                i < fullStars
                  ? "text-amber-400"
                  : i === fullStars && hasHalf
                  ? "text-amber-300"
                  : "text-gray-200"
              }`}
            />
          ))}
        </div>
        <span className="text-sm font-semibold text-gray-800">
          {firm.rating.toFixed(1)}
        </span>
        <span className="text-sm text-gray-400">
          ({firm.ratingCount} opinii)
        </span>
      </div>
      ) : null}

      {/* Certifications */}
      <div className="mb-4 flex flex-wrap gap-1.5">
        {firm.certs.map((cert) => (
          <span
            key={cert}
            className="flex items-center gap-1 rounded-md bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
          >
            <CheckBadgeIcon className="h-3.5 w-3.5" />
            {cert}
          </span>
        ))}
      </div>

      {/* Open hours */}
      {firm.openHours && (
        <div className="mb-3 flex items-center gap-1.5 text-sm text-gray-500">
          <ClockIcon className="h-4 w-4 shrink-0 text-gray-400" />
          <span>{firm.openHours}</span>
        </div>
      )}

      {/* Address & price */}
      <div className="mb-4 space-y-1.5 text-sm text-gray-500">
        <div className="flex items-start gap-1.5">
          <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
          <span>{firm.address}</span>
        </div>
        {firm.priceRange && (
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-gray-700">{firm.priceRange}</span>
            {firm.priceNote && (
              <span className="text-gray-400">· {firm.priceNote}</span>
            )}
          </div>
        )}
      </div>

      {/* CTAs */}
      <div className="mt-auto flex gap-2">
        {firm.phone ? (
          <a
            href={`tel:${firm.phone.replace(/\s/g, "")}`}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
          >
            <PhoneIcon className="h-4 w-4" />
            {firm.phone}
          </a>
        ) : firm.website ? (
          <a
            href={firm.website}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
          >
            <GlobeAltIcon className="h-4 w-4" />
            Odwiedź stronę
          </a>
        ) : (
          <span className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-100 px-3 py-2 text-sm text-gray-400">
            Brak kontaktu
          </span>
        )}
        {firm.phone && firm.website && (
          <a
            href={firm.website}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-500 transition-colors hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
            title="Strona www"
          >
            <GlobeAltIcon className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
};

export { FirmCard };
